import { Grid } from '@material-ui/core';
import SchoolOutlined from '@material-ui/icons/SchoolOutlined';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { showPrintBarcodeDialog } from 'src/barcode/print-barcode';
import { postOpenCashDrawer } from 'src/cash-drawer/business-logic/service/post-open-cash-drawer';
import { isCashOfficeButtonEnabled } from 'src/cash-office-display/business-logic/cash-office-action-buttons';
import { HandleCashOfficeActions } from 'src/cash-office-display/business-logic/handle-cash-office-actions';
import { openDrawer } from 'src/cash-office-display/components/cash-drawer-actions';
import { checkCommandPermission } from 'src/command-menu/permissions/show-permissions-modal';
import { LocalStorage } from 'src/common/helpers/local-storage';
import { ICommandConfiguration } from 'src/common/interfaces/dtos/ICommandsGroup';
import { ILoginUser } from 'src/common/interfaces/dtos/ILoginUser';
import { ICommand, IPosUiMenu } from 'src/common/interfaces/states/IPosUiMenuState';
import { ReactComponent as Alert } from 'src/common/media/action-required.svg';
import { AppState } from 'src/common/redux/configure-store';
import { trainingModeStyles } from 'src/common/training-mode/training-mode-styles';
import { showDeviceStatusDialog } from 'src/device-status/device-status';
import { forgotPassword } from '../../../reset-password/business-logic/forgot-password';
import {
  LoginMenuPanelItem,
  LoginMenuPanelItemProps,
} from '../login-menu-panel-item/login-menu-panel-item';
import MenuFooter from '../menu-footer';
import { styles } from './styles';

interface LoginMenuProps {
  trainingModeActive: boolean;
  toggleMenuDisplay: () => void;
  toggleTrainingMode: () => void;
  registerModeActive: boolean;
}

type Props = LoginMenuProps & ILoginMenuState;

const LoginMenu = ({
  trainingModeActive,
  resetUserPassword,
  toggleMenuDisplay,
  toggleTrainingMode,
  registerModeActive,
  menuConfig,
  deviceNeedsAttention,
}: Props): JSX.Element => {
  const classes = styles();
  const trainingModeClasses = trainingModeStyles();

  const DeviceStatusIcon = (): JSX.Element => {
    return <Alert className={classes.deviceAlertIcon} />;
  };

  const { t } = useTranslation();

  const actions: { [key: string]: (creds?: ILoginUser) => Promise<void> | void } = {
    OpenCashDrawerOnSignoffCommand: (creds?: ILoginUser) => {
      postOpenCashDrawer(creds ? creds : {}).then(() => {
        openDrawer();
      });
      toggleMenuDisplay();
    },
    TillClosingCommand: () => {
      HandleCashOfficeActions.performTillClosing();
      toggleMenuDisplay();
    },
    ReInitDevicesCommand: () => {
      showDeviceStatusDialog();
    },
    ForgotPasswordCommand: forgotPassword,
    CashierBarcodeCommand: showPrintBarcodeDialog,
  };

  const isDisabled = {
    TillClosingCommand: !isCashOfficeButtonEnabled('till-closing-login-button'),
    OpenCashDrawerOnSignoffCommand: !isCashOfficeButtonEnabled('open-cash-drawer-login-button'),
    ForgotPasswordCommand: trainingModeActive,
  };

  const configurationMenuItemsProps: LoginMenuPanelItemProps[] = menuConfig.commands.map(
    btnConf => {
      let buttonIcon: (() => JSX.Element) | undefined;
      switch (btnConf.commandId) {
        case 'ReInitDevicesCommand':
          buttonIcon = deviceNeedsAttention ? DeviceStatusIcon : undefined;
          break;
      }
      const item: LoginMenuPanelItemProps = {
        key: btnConf.commandId,
        button: {
          'data-testid': `${btnConf.commandId}-button`,
          disabled: isDisabled[btnConf.commandId],
          className: `${classes.loginMenuButton}`,
          centerRipple: true,
          onClick: () => onLoginMenuCommandClick(btnConf, actions[btnConf.commandId]),
          classes: {
            disabled: classes.loginMenuDisabledItem,
          },
        },
        label: {
          className: `${classes.loginOptionHeading}`,
          code: t(btnConf.commandId),
        },
        subLabel: {
          className: `${classes.loginOptionSubHeading}`,
          code: t(`${btnConf.commandId}_SubLabel`),
        },
        icon: {
          icon: buttonIcon,
        },
      };
      return item;
    },
  );

  return (
    <>
      <Grid
        item
        className={`flexFull ${
          trainingModeActive ? trainingModeClasses.trainingModeBackground : ''
        }`}
      >
        <Grid container direction='column' className={classes.configurationMenuContainer}>
          {configurationMenuItemsProps.map(LoginMenuPanelItem)}
        </Grid>
      </Grid>
      <MenuFooter
        trainingModeActive={trainingModeActive}
        buttons={[
          {
            label: trainingModeActive ? t('SignInView_CancelTrainingMode') : t('TrainingMode'),
            'data-testid': 'training-mode-btn',
            className: 'logOutButton',
            onClick: () => {
              toggleTrainingMode();
              toggleMenuDisplay();
            },
            color: 'default',
            buttonType: 'white',
            startIcon: <SchoolOutlined />,
            disabled: registerModeActive || !!LocalStorage.getLockUser() || resetUserPassword,
          },
          {
            label: t('HidePanel'),
            'data-testid': 'hide-btn',
            className: 'hideButton',
            onClick: toggleMenuDisplay,
          },
        ]}
      />
    </>
  );
};

const onLoginMenuCommandClick = async (
  button: ICommand,
  action: (creds?: ILoginUser) => void | Promise<void>,
) => {
  const commandConfigurationButton = button as ICommandConfiguration;
  commandConfigurationButton.name = button.commandId;
  if (commandConfigurationButton.name === 'OpenCashDrawerOnSignoffCommand') {
    button.checkRole = true;
  }
  if (button.checkRole) {
    const creds = await checkCommandPermission(commandConfigurationButton, true);
    if (creds !== undefined) {
      action(creds);
    }
  } else {
    action();
  }
};

interface ILoginMenuState {
  resetUserPassword: boolean;
  menuConfig: IPosUiMenu;
  deviceNeedsAttention: boolean;
}

const mapStateToProps = (state: AppState): ILoginMenuState => ({
  resetUserPassword: state.resetPassword.showResetPassword,
  menuConfig: state.posUiMenu.LogOff,
  deviceNeedsAttention: !!state.deviceStatus.devices.find(({ warning }) => warning),
});
export default connect(mapStateToProps)(LoginMenu);
